import {
  VIEWER_TYPES,
  eventNames,
} from '../screens/business-profile-page/constants/fpti-constants'
import { sanitizeUrl } from '@braintree/sanitize-url'
import { trackClicksFromElementData } from './fpti'

// TYPES
import type { AxiosError, HttpStatusCode } from 'axios'
import type { EventTypes } from '@/types'
export type HttpResponseError = AxiosError & { statusCode: HttpStatusCode }

/**
 * Check for 404 not found status
 */
export function isNotFoundError(err: HttpResponseError): boolean {
  return err.status === 404 || err.statusCode === 404
}

/**
 *
 * @param {boolean} canEdit - profile owner
 * @param {boolean} userLoggedIn - logged-in user
 * @param {string} eventName - event name
 * @returns {Object} - event name and type appended with viewer type
 */
export function getEventNameAndType(
  canEdit: boolean,
  userLoggedIn: boolean,
  eventName: string,
): { eventName: string; eventType: EventTypes } {
  let viewerType = VIEWER_TYPES.GUEST
  if (userLoggedIn) {
    viewerType = canEdit ? VIEWER_TYPES.SELLER : VIEWER_TYPES.BUYER
  }
  return {
    eventName: eventNames[eventName + viewerType],
    eventType: (eventName + viewerType) as EventTypes,
  }
}

/**
 * Returns sanitized URL parameters
 */
export function sanitizeUrlParams(...params: string[]): any[] {
  const sanitizedParams: string[] = []
  params.forEach((param) => {
    sanitizedParams.push(sanitizeUrl(param))
  })
  return sanitizedParams
}

/**
 * Resize an image to the given dimensions
 * @param dataAsUrl image in data url format
 * @param dimensions min/max width/height
 */
export function resizeImage(
  dataAsUrl: string,
  dimensions: {
    minPixelWidth: number
    minPixelHeight: number
    maxPixelWidth: number
    maxPixelHeight: number
  },
): Promise<string> {
  return new Promise((resolve, reject) => {
    const newImage = new Image()
    newImage.src = dataAsUrl
    newImage.onerror = reject
    newImage.onload = () => {
      try {
        const { minPixelWidth, minPixelHeight, maxPixelWidth, maxPixelHeight } =
          dimensions

        const { width, height } = newImage

        let finalWidth = width < minPixelWidth ? minPixelWidth : width
        let finalHeight = height < minPixelHeight ? minPixelHeight : height

        if (width > height) {
          if (width > maxPixelWidth) {
            finalHeight *= maxPixelWidth / width
            finalWidth = maxPixelWidth
          }
        } else {
          if (height > maxPixelHeight) {
            finalWidth *= maxPixelHeight / height
            finalHeight = maxPixelHeight
          }
        }

        // draw new image
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')

        canvas.width = finalWidth
        canvas.height = finalHeight

        context.drawImage(newImage, 0, 0, finalWidth, finalHeight)

        resolve(canvas.toDataURL())
      } catch (e) {
        reject(e)
      }
    }
  })
}

/**
 * Wraps keyboard handler to only invoke callback when
 * enter or space key is pressed
 */
export function invokeOnSpaceOrEnter(
  cb: (event: React.KeyboardEvent<HTMLElement>) => void,
) {
  return (event: React.KeyboardEvent<HTMLElement>) => {
    const { code } = event

    if (code === 'Enter' || code === 'Space') {
      trackClicksFromElementData(event)

      cb(event)
    }
  }
}

/**
 * Attempt to parse string or return an empty object if it fails
 */
export function parseSafely(toParse: string) {
  try {
    return JSON.parse(toParse)
  } catch (_e) {
    return {}
  }
}
